import { getPackageMetadata } from "@amzn/aws-console-lotus-midway-metadata-client";
import {
  createFailedResult,
  createSucceededResult,
  getPackageMajorVersion,
  ImportPackageErrorCode,
  Result,
} from "@amzn/aws-console-lotus-web-client-common";
import { PackageMetadataResponse } from "@amzn/aws-console-lotus-data-plane-interface";
import { getEntryScriptElement, getPackageExports } from "./entryScript";
import {
  injectResources,
  LINK_ATTRIBUTE,
  SCRIPT_ATTRIBUTE,
} from "./resourceElement";
import type { ImportPackageInput, ImportPackageResult } from "./types";

const isPackageAlreadyImported = (
  ownerDocument: Document,
  { packageName, majorVersion }: ImportPackageInput
): boolean => {
  const packageMajorVersion = getPackageMajorVersion(packageName, majorVersion);
  // Check if package entry script or any other resource (script/link) corresponding
  // to given package name and majorVersion exists in the DOM
  return Boolean(
    getEntryScriptElement(ownerDocument, packageName, majorVersion) ||
      ownerDocument.querySelector(
        `script[${SCRIPT_ATTRIBUTE}="${packageMajorVersion}"]`
      ) ||
      ownerDocument.querySelector(
        `link[${LINK_ATTRIBUTE}="${packageMajorVersion}"]`
      )
  );
};

const metadataHasModuleOrAssets = (
  metadata: PackageMetadataResponse["metadata"]
): boolean => {
  return Boolean(metadata.module || metadata.assets?.length);
};

const doImport = async (
  input: ImportPackageInput & { ownerDocument: Document }
): Promise<Result<ImportPackageErrorCode>> => {
  let metadataResponse: PackageMetadataResponse;
  try {
    metadataResponse = await getPackageMetadata({
      packageName: input.packageName,
      majorVersion: input.majorVersion,
    });
    console.log("[LOTUS] Metadata Response: ", metadataResponse);
  } catch (error) {
    return createFailedResult({
      code: ImportPackageErrorCode.GetPackageMetadataFailed,
      message: `getPackageMetadata() failed`,
      cause: error,
    });
  }

  if (!metadataHasModuleOrAssets(metadataResponse.metadata)) {
    return createFailedResult({
      code: ImportPackageErrorCode.InvalidPackageMetadata,
      message: `getPackageMetadata() response does not contain module or assets`,
    });
  }

  return injectResources({
    ...input,
    metadataResponse,
  });
};

export const importPackage = async <PackageExportsT>(
  input: ImportPackageInput
): Promise<ImportPackageResult<PackageExportsT>> => {
  try {
    const { packageName, majorVersion, ownerDocument = document } = input;

    if (isPackageAlreadyImported(ownerDocument, input)) {
      return createSucceededResult({
        packageExports: getPackageExports(
          ownerDocument,
          packageName,
          majorVersion
        ),
      });
    }

    const result = await doImport({
      ...input,
      ownerDocument,
    });
    if (!result.succeeded) {
      return result;
    }

    return createSucceededResult({
      packageExports: getPackageExports(
        ownerDocument,
        packageName,
        majorVersion
      ),
    });
  } catch (error) {
    const errorCode = ImportPackageErrorCode.Unknown;
    const errorMessage = `Unexpected error while importing package with configuration: ${JSON.stringify(
      input
    )}`;

    return createFailedResult({
      code: errorCode,
      message: errorMessage,
      cause: error,
    });
  }
};
